/**
 * A single, kind-agnostic handle on a construction-layer object (issue
 * #545, Phase 2). Construction lines, tracing images, and groups are three
 * peers that share most interactions — select, move, rotate, group, delete,
 * arrange, flip — yet each was wired up its own parallel path, so every new
 * feature had to be implemented two or three times. `ObjectRef` is the
 * common handle those generic interactions key off of, and this module holds
 * the **pure model** walkers over it (no Paper, no DOM — fully unit-testable).
 *
 * The three selection axes on `SceneSelection` (`constructionIds`,
 * `constructionGroupIds`, `tracingImageIds`) stay the source of truth;
 * `Scene.selectedObjects()` is a read-only projection of them into refs. This
 * module is purely additive — it introduces no behaviour change on its own.
 */
import {
  descendantConstructionImageIds,
  descendantConstructionLineIds,
  groupAncestorIds,
  groupForConstructionLine,
  groupForTracingImage,
  imageAncestorIds,
  lineAncestorIds,
  projectConstructionGroups,
  type ConstructionGroup,
  type Project,
} from "./Project";

export type ObjectKind = "line" | "image" | "group";

/** A kind-tagged id for one construction-layer object. */
export interface ObjectRef {
  kind: ObjectKind;
  id: string;
}

export const lineRef = (id: string): ObjectRef => ({ kind: "line", id });
export const imageRef = (id: string): ObjectRef => ({ kind: "image", id });
export const groupRef = (id: string): ObjectRef => ({ kind: "group", id });

/** Stable string key for de-duping refs across the selection axes. */
export function objectRefKey(ref: ObjectRef): string {
  return `${ref.kind}:${ref.id}`;
}

export function objectRefsEqual(a: ObjectRef, b: ObjectRef): boolean {
  return a.kind === b.kind && a.id === b.id;
}

/**
 * The leaf objects a ref expands to for a geometric operation (move /
 * rotate / flip / delete). A group fans out over every line and image
 * reachable through it (recursively, via `descendantConstruction*Ids`);
 * a line or image is its own single leaf. Child *groups* are not returned
 * — they're containers, and their leaves are already included. Order is
 * lines first (in descendant order), then images, matching how the rest of
 * the construction code enumerates a group's members.
 */
export function descendantObjectRefs(
  project: Project,
  ref: ObjectRef,
): ObjectRef[] {
  if (ref.kind === "line" || ref.kind === "image") return [ref];
  const out: ObjectRef[] = [];
  for (const lid of descendantConstructionLineIds(project, ref.id)) {
    out.push(lineRef(lid));
  }
  for (const iid of descendantConstructionImageIds(project, ref.id)) {
    out.push(imageRef(iid));
  }
  return out;
}

/**
 * A group's leaf descendants partitioned by kind in a single walk — the
 * ergonomic façade over `descendantObjectRefs` for the very common "every
 * line and image inside this group" need that code used to express by
 * pairing `descendantConstructionLineIds` with `descendantConstructionImageIds`
 * side by side. Centralising it means "what are a group's members" lives in
 * one place — if a third leaf kind were ever added, only this changes. Lines
 * come first (descendant order), then images.
 */
export function descendantLeafIds(
  project: Project,
  groupId: string,
): { lineIds: string[]; imageIds: string[] } {
  const lineIds: string[] = [];
  const imageIds: string[] = [];
  for (const ref of descendantObjectRefs(project, groupRef(groupId))) {
    if (ref.kind === "line") lineIds.push(ref.id);
    else if (ref.kind === "image") imageIds.push(ref.id);
  }
  return { lineIds, imageIds };
}

/**
 * The group that directly owns `ref`, or null when it sits at the top
 * level. Dispatches to the per-kind membership index: lines via `lineIds`,
 * images via `imageIds`, groups via the parent's `groupIds`.
 */
export function groupForObjectRef(
  project: Project,
  ref: ObjectRef,
): ConstructionGroup | null {
  if (ref.kind === "line") return groupForConstructionLine(project, ref.id);
  if (ref.kind === "image") return groupForTracingImage(project, ref.id);
  for (const g of projectConstructionGroups(project)) {
    if ((g.groupIds ?? []).includes(ref.id)) return g;
  }
  return null;
}

/**
 * Top-down ancestor-group chain of `ref` — `[topmost, …, direct parent]`,
 * excluding the ref itself; empty when it's at the top level. Unifies
 * `lineAncestorIds` / `imageAncestorIds` / `groupAncestorIds`.
 */
export function objectRefAncestorIds(
  project: Project,
  ref: ObjectRef,
): string[] {
  if (ref.kind === "line") return lineAncestorIds(project, ref.id);
  if (ref.kind === "image") return imageAncestorIds(project, ref.id);
  return groupAncestorIds(project, ref.id);
}
